'use client';

/**
 * Pathname-aware pill rendering for streaming-style headers. Ported
 * from ThemeMaker's
 * {@code lms-portal/src/components/shell/PillActiveMenu.tsx}.
 *
 * <p>The cinematic-immersive header renders its menu as a "floating
 * capsule" of pill-shaped links — one is the active item (filled
 * brand-tinted pill) and the rest are plain bright-on-dark text links.
 *
 * <p>Static {@code active: true} flag in the shell config decides
 * which item is "active" by default — typically Home. But shell config
 * is shared across every page in a tenant, so without runtime
 * resolution Home stays pilled even after the user navigates to
 * {@code /movies}. This component fixes that by reading
 * {@code usePathname()} and picking the most-specific href match.
 *
 * <p>SSR-safe: server returns the static-active rendering. After
 * hydration, the client recomputes if a different item matches the
 * pathname. One frame of flicker on first paint of a non-home page —
 * acceptable because the header is far above the fold and the flicker
 * is just a pill background swap (no layout shift).
 */
import { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { t } from '../../portal';
import type { PillMenuItem } from '../menu/pillMenu';

export interface PillActiveMenuLinksProps {
  /** Items already filtered by the server for visibility (auth/perms).
   *  Passed in serializable shape (no functions/components). */
  items: PillMenuItem[];
  tenantSlug: string;
  /** Tenant-scoped href builder result is precomputed; we just receive
   *  the final URLs. The server applied {@code tenantHref} already. */
  resolvedHrefs: string[];
  locale: string;
  fallbackLocale: string;
}

/**
 * Match algorithm:
 * <ol>
 *   <li>Most-specific match wins. If items are {@code /home, /movies,
 *       /movies/action} and pathname is {@code /movies/action}, then
 *       {@code /movies/action} wins (longer prefix).</li>
 *   <li>Tenant-prefix-aware. The href stored in shell config is
 *       tenant-relative; the actual pathname includes the tenant slug.
 *       Strip the slug before comparing.</li>
 *   <li>Trailing slashes normalized — treat {@code /movies} and
 *       {@code /movies/} as the same.</li>
 *   <li>When nothing matches, the static {@code active} flag from
 *       shell config wins. Keeps Home pilled on the home page even
 *       when its href is {@code /} vs {@code /home} (which a strict
 *       path match would mishandle).</li>
 * </ol>
 */
function pickActiveIndex(
  items: PillActiveMenuLinksProps['items'],
  pathname: string,
  tenantSlug: string,
): number {
  const tenantPrefix = `/${tenantSlug}`;
  let relPath = pathname;
  if (relPath.startsWith(tenantPrefix)) {
    relPath = relPath.slice(tenantPrefix.length) || '/';
  }
  if (relPath.length > 1 && relPath.endsWith('/')) {
    relPath = relPath.slice(0, -1);
  }

  let bestIdx = -1;
  let bestLen = -1;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (!item) continue;
    const href = item.href;
    if (!href || typeof href !== 'string') continue;
    let candidate = href;
    if (candidate.length > 1 && candidate.endsWith('/')) {
      candidate = candidate.slice(0, -1);
    }
    const isMatch =
      relPath === candidate || (candidate !== '/' && relPath.startsWith(candidate + '/'));
    if (isMatch && candidate.length > bestLen) {
      bestIdx = i;
      bestLen = candidate.length;
    }
  }

  if (bestIdx >= 0) return bestIdx;

  for (let i = 0; i < items.length; i++) {
    if (items[i]?.active === true) return i;
  }
  return -1;
}

export function PillActiveMenuLinks({
  items,
  tenantSlug,
  resolvedHrefs,
  locale,
  fallbackLocale,
}: PillActiveMenuLinksProps) {
  const pathname = usePathname() ?? '';
  const activeIdx = useMemo(
    () => pickActiveIndex(items, pathname, tenantSlug),
    [items, pathname, tenantSlug],
  );

  return (
    <>
      {items.map((item, i) => {
        const isActive = i === activeIdx;
        const labelText = t(item.label, locale, fallbackLocale);
        const badgeText = item.badge ? t(item.badge, locale, fallbackLocale) : null;
        return (
          <a
            key={i}
            href={resolvedHrefs[i]}
            aria-current={isActive ? 'page' : undefined}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 'var(--font-size-sm, 14px)',
              fontWeight: isActive ? 600 : 500,
              padding: isActive ? '6px 14px' : '6px 8px',
              borderRadius: 999,
              background: isActive ? 'rgba(255,255,255,0.14)' : 'transparent',
              color: isActive ? '#fff' : 'rgba(255,255,255,0.78)',
              textDecoration: 'none',
              transition: 'background 150ms ease, color 150ms ease',
            }}
          >
            {labelText}
            {badgeText && (
              <span
                style={{
                  background: 'var(--color-brand-500, #e50914)',
                  color: 'var(--color-text-on-brand, #fff)',
                  fontSize: '9px',
                  fontWeight: 700,
                  padding: 'var(--space-px, 1px) var(--space-1, 4px)',
                  borderRadius: 'var(--radius-sm, 4px)',
                  letterSpacing: 'var(--font-tracking-wider, 0.04em)',
                }}
              >
                {badgeText}
              </span>
            )}
          </a>
        );
      })}
    </>
  );
}
